import { FC, useMemo } from 'react';
import { beautifyThePrice, isNotEmpty } from '@lhb/func';
import { fixNumber } from '@/common/utils/ways';
import { useMethods } from '@lhb/hook';
import { CommercialConditionDetail } from '../CommercialCondition';
import { CostAnalysis, SmartDecisionSummary, SmartDecisionValue } from './types';
import cs from 'classnames';
import styles from './index.module.less';
import V2Table from '@/common/components/Data/V2Table';

interface CostPaybackAnalysisProps {
  costAnalysis?: CostAnalysis;
  summary?: SmartDecisionSummary;
  /** 商务条件详情，与商务条件组件使用同一份数据。 */
  commercialConditionDetail?: CommercialConditionDetail;
}

interface FinancialSummaryItem {
  label: string;
  value: string;
}

/**
 * @description 成本与回本分析
 */
const CostPaybackAnalysis: FC<CostPaybackAnalysisProps> = ({
  costAnalysis,
  summary,
  commercialConditionDetail,
}) => {
  const methods = useMethods({
    formatNumber(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return beautifyThePrice(value || '', ',', 'auto');
    },
    formatMoney(value: SmartDecisionValue, suffix: string) {
      if (!isNotEmpty(value)) return '-';
      return `${methods.formatNumber(value)}${suffix}`;
    },
    formatWan(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return String(value);
      // 绝对值达到一万时按“万”为单位展示，保留一位小数，负数同样按万展示（如 -78.9万）
      if (Math.abs(numberValue) >= 10000) return `${fixNumber(numberValue / 10000, 1)}万`;
      return methods.formatNumber(value);
    },
    formatPercent(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return `${value}%`;
    },
    getBusinessTypeText(value: SmartDecisionValue) {
      const businessTypeText: Record<string, string> = {
        '1': '按租金',
        '2': '按扣点',
        '3': '租金+扣点',
        '4': '二者取高',
      };
      return businessTypeText[String(value)] || '';
    },
    getMonthlyRentTitle() {
      const businessTypeText = methods.getBusinessTypeText(commercialConditionDetail?.businessType);
      return businessTypeText ? `租金及费用（${businessTypeText}）` : '租金及费用';
    },
    renderMoney(value: SmartDecisionValue) {
      return methods.formatMoney(value, '元');
    },
    renderMonthlyMoney(value: SmartDecisionValue) {
      return methods.formatMoney(value, '元/月');
    },
    renderPositiveMoney(value: SmartDecisionValue) {
      return <span className={styles.positiveValue}>{methods.formatMoney(value, '元')}</span>;
    },
    renderPositiveMonthlyMoney(value: SmartDecisionValue) {
      return <span className={styles.positiveValue}>{methods.formatMoney(value, '元/月')}</span>;
    },
    getOneTimeColumns() {
      return [
        {
          key: 'exhibitionDesignFee',
          title: '展具设计',
          render: methods.renderMoney,
        },
        {
          key: 'constructionFee',
          title: '运输搭建',
          render: methods.renderMoney,
        },
        {
          key: 'goodsCostInvestment',
          title: '首批货品',
          render: methods.renderMoney,
        },
        {
          key: 'oneTimeInvestment',
          title: '一次性投入合计',
          render: methods.renderPositiveMoney,
        },
      ];
    },
    getMonthlyColumns() {
      return [
        {
          key: 'monthlySales',
          title: '预估月销售额',
          width: 185,
          render: methods.renderMonthlyMoney,
        },
        {
          key: 'monRentAndExpense',
          title: methods.getMonthlyRentTitle(),
          width: 185,
          render: methods.renderMonthlyMoney,
        },
        {
          key: 'laborCost',
          title: '人力成本',
          width: 185,
          render: methods.renderMonthlyMoney,
        },
        {
          key: 'goodsCost',
          title: '货品成本',
          width: 185,
          render: methods.renderMonthlyMoney,
        },
        {
          key: 'monNetProfit',
          dataIndex: 'monNetProfit',
          title: '月净利润',
          render: methods.renderPositiveMonthlyMoney,
        },
      ];
    },
    loadOneTimeTable() {
      const dataSource = costAnalysis ? [{ ...costAnalysis, id: 'one-time' }] : [];
      return Promise.resolve({
        dataSource,
        count: dataSource.length,
      });
    },
    loadMonthlyTable() {
      const dataSource = costAnalysis ? [{ ...costAnalysis, id: 'monthly' }] : [];
      return Promise.resolve({
        dataSource,
        count: dataSource.length,
      });
    },
    getFinancialSummary(): FinancialSummaryItem[] {
      return [
        {
          label: '月预估净利润',
          value: methods.formatWan(costAnalysis?.monNetProfit ?? summary?.monNetProfit),
        },
        {
          label: '净利率',
          value: methods.formatPercent(summary?.netProfitRate),
        },
        {
          label: '预计回本周期',
          value: methods.formatMoney(summary?.paybackPeriod, '个月'),
        },
      ];
    },
    renderFinancialSummary(item: FinancialSummaryItem) {
      return (
        <div
          className={styles.financialSummaryCard}
          key={item.label}
        >
          <strong className='bold'>{item.value}</strong>
          <span>{item.label}</span>
        </div>
      );
    },
  });

  /**
   * 成本数据由父级异步下发，V2Table 只在挂载时请求一次，
   * 这里通过变化 filters 的引用，在 costAnalysis 到达后触发 V2Table 重新请求。
   */
  const tableFilters = useMemo(() => ({ costAnalysis }), [costAnalysis]);

  return (
    <div className={styles.detailCard}>
      <div className={styles.chartTitle}>成本与回本分析</div>
      <div className={styles.costSectionTitle}>一次性投入</div>
      <V2Table
        rowKey='id'
        defaultColumns={methods.getOneTimeColumns()}
        onFetch={methods.loadOneTimeTable}
        // 成本数据异步到达后通过变更 filters 触发 V2Table 重新请求
        filters={tableFilters}
        pagination={false}
        emptyRender
        hideColumnPlaceholder
        className={styles.costTable}
      />
      <div className={cs(styles.costSectionTitle, styles.month)}>月度收支</div>
      <V2Table
        rowKey='id'
        defaultColumns={methods.getMonthlyColumns()}
        onFetch={methods.loadMonthlyTable}
        // 成本数据异步到达后通过变更 filters 触发 V2Table 重新请求
        filters={tableFilters}
        pagination={false}
        emptyRender
        hideColumnPlaceholder
        className={styles.costTable}
      />
      <div className={cs(styles.financialSummaryGrid, styles.isCost)}>
        {methods.getFinancialSummary().map(methods.renderFinancialSummary)}
      </div>
    </div>
  );
};

export default CostPaybackAnalysis;
